import React from 'react';

/**
 * The pill-shaped switch used by the contact page ("تواصل معنا" / "إرسال شكوى")
 * and, in its underline form, by the certificate detail nav.
 *
 * Controlled: the parent owns `value`, which keeps the choice available for a
 * URL query param or a form reset without this component knowing about either.
 *
 * @param {{id: string, label: React.ReactNode}[]} items
 * @param {'pill'|'underline'} variant
 */
const Tabs = ({ items, value, onChange, variant = 'pill', className = '' }) => {
    const isPill = variant === 'pill';

    // Arrow keys move between tabs, as a tablist is expected to.
    const handleKeyDown = (event) => {
        const step = { ArrowRight: 1, ArrowLeft: -1 }[event.key];
        if (!step) return;

        event.preventDefault();
        const currentIndex = items.findIndex(item => item.id === value);
        // In RTL the right arrow moves towards the *earlier* tab. The direction
        // is read off the tablist itself rather than `document.dir`: the module
        // sets `dir` on its own root (see V2Root) and never touches <html>, so
        // the document's own direction is blank and would answer for `ltr`.
        const isRtl = window.getComputedStyle(event.currentTarget).direction === 'rtl';
        const direction = isRtl ? -step : step;
        const next = (currentIndex + direction + items.length) % items.length;
        onChange(items[next].id);
    };

    return (
        <div
            role="tablist"
            onKeyDown={handleKeyDown}
            className={
                // Pill shell, from Figma node 16383:29835: white on a 1px
                // #f5f7fa edge, 5.8px of padding around a 6px gap, and a soft
                // shadow under the shell. The active tab casts none of its own.
                (isPill
                    ? 'tw-inline-flex tw-gap-1.5 tw-rounded-full tw-border tw-border-v2-ground-200 tw-bg-white tw-p-[5.8px] tw-shadow-v2-switch'
                    : 'tw-flex tw-gap-8 tw-border-b tw-border-v2-ground-500 tw-overflow-x-auto') +
                ' ' + className
            }
        >
            {items.map(item => {
                const isActive = item.id === value;

                return (
                    <button
                        key={item.id}
                        type="button"
                        role="tab"
                        id={`tab-${item.id}`}
                        aria-selected={isActive}
                        aria-controls={`panel-${item.id}`}
                        // Only the active tab is reachable by Tab; arrows do the rest.
                        tabIndex={isActive ? 0 : -1}
                        onClick={() => onChange(item.id)}
                        className={[
                            'tw-whitespace-nowrap tw-font-bold tw-transition-colors tw-duration-200',
                            'focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-v2-brand',
                            // 190px wide and 59.5 tall at the design's size,
                            // with 13.5px bold labels. Both the width floor and
                            // the height relax below `md`, where two 190px tabs
                            // plus their padding no longer fit a phone.
                            isPill
                                ? `tw-rounded-full tw-px-7 tw-text-[13.5px] tw-h-11 md:tw-h-[59.5px] md:tw-min-w-[190px] ${
                                    isActive
                                        ? 'tw-bg-v2-brand tw-text-white'
                                        : 'tw-text-[#283042] hover:tw-text-v2-brand'
                                }`
                                : `tw--mb-px tw-border-b-2 tw-px-1 tw-py-4 tw-text-v2-label ${
                                    isActive
                                        ? 'tw-border-v2-brand tw-text-v2-brand'
                                        : 'tw-border-transparent tw-text-v2-text-secondary hover:tw-text-v2-text-heading'
                                }`,
                        ].join(' ')}
                    >
                        {item.label}
                    </button>
                );
            })}
        </div>
    );
};

export default Tabs;
